import { Injectable } from '@angular/core';
import { Agency, AGENCY_TYPE, AgencyKeys, Agent, AGENT_TYPE, AgentKeys, Lookup } from '@ag-common-lib/public-api';
import { AgencyService } from '../../../../services/agency.service';
import { BehaviorSubject, from, Observable } from 'rxjs';
import { BaseFormService } from '../../../../utils/base-form-service';
import { AgencyHeaderKeys } from '../agency-header/agency-header.model';
import { map } from 'rxjs/operators';
import { AGENTS_FIRESTORE_COLLECTION, AgentService } from '../../../../services/agent.service/agent.service';
import { QueryParam, WhereFilterOperandKeys } from '../../../../dao/CommonFireStoreDao.dao';
import { ToastrService } from 'ngx-toastr';
import { PromptModals } from 'ag-common-svc/lib/components/prompt/prompt.model';
import { toSignal } from '@angular/core/rxjs-interop';
import { BaseModelKeys } from '@ag-common-lib/lib/models/base.model';
import { pick } from 'lodash';
import {
  AGENCY_SYSTEM_DIRECTORY_NAMES,
  AgencyFileDirectory,
  AgencyFileDirectoryKeys,
} from '@ag-common-lib/lib/models/utils/agency-file-directory.model';
import { FileDirectoryKeys } from '@ag-common-lib/lib/models/utils/file-directory.model';
import { AgencyFileDirectoriesService, ChangeSourceTypeService } from 'ag-common-svc/public-api';
import { AgentSearchService } from 'ag-common-svc/lib/services/agent-search';
import { AgencyHierarchyManagerMode } from 'ag-common-svc/lib/modules/agency-hierarchy-administration/agency-hierarchy-management/agency-hierarchy-management.model';
import { AgentsTransferModalService } from 'ag-common-svc/lib/modules/agency-hierarchy-administration/agency-hierarchy-management/agents-transfer-modal.service';
import { ModalWindowUserAction } from 'ag-common-svc/lib/components/modal-window/modal-window.model';
import {
  BatchCommand,
  BatchCommandKeys,
  BatchCommandType,
  BatchOperationsService,
} from 'ag-common-svc/lib/dao/batch-firestore.service';


@Injectable()
export class AgencyInfoModalService extends BaseFormService<Agency> {
  private readonly _isTransferInProgress$ = new BehaviorSubject<boolean>(false);
  isTransferInProgress$ = this._isTransferInProgress$.asObservable();

  // Sorted by the server. The option this replaces asked for sortField 'name', which no
  // agent document has — the DAO sorts client-side, so that sort silently did nothing.
  rmds$ = from(
    this.agentSearchService.search({
      filter: [AgentKeys.is_rmd, '=', true],
      sort: [{ selector: AgentKeys.p_agent_first_name, desc: false }],
    }),
  ).pipe(map(response => (response?.data ?? []).map(this.mapAgentByName)));
  mgasLookup$ = this.agencyService.mgasLookup$;
  protected agenciesSig = toSignal(this.agencyService.agencies$);

  private allAgencyLeads$ = from(
    this.agentSearchService.search({
      filter: [AgentKeys.agent_type, '<>', AGENT_TYPE.GENERAL_AGENT],
      sort: [{ selector: AgentKeys.p_agent_first_name, desc: false }],
    }),
  ).pipe(map(response => (response?.data ?? []).map(this.mapAgentByName)));
  allAgencyLeadsSig = toSignal(this.allAgencyLeads$);
  selectedAgencyStatus$ = new BehaviorSubject<Lookup>(null);

  readonly promptModalId = PromptModals.reassignAgencyManagerModal;

  constructor(
    private readonly agencyService: AgencyService,
    private agentSearchService: AgentSearchService,
    private toastrService: ToastrService,
    private agencyFileDirectoriesService: AgencyFileDirectoriesService,
    private agentsTransferModalService: AgentsTransferModalService,
    private changeSourceTypeService: ChangeSourceTypeService,
    private batchOperationsService: BatchOperationsService,
    // private agencyHierarchyManagementService: AgencyHierarchyManagementService,
  ) {
    super();
  }

  getFormData = (agency: Partial<Agency>) => {
    const primaryEmailAddress = agency?.[AgencyKeys.officeEmailAddress]?.find(email => email?.is_primary);
    let primaryShippingAddress = null;
    agency?.[AgencyKeys.addresses]?.forEach(address => {
      if (address.is_primary_shipping) {
        primaryShippingAddress = address;
      }
    });
    const primaryPhoneNumber = agency?.[AgencyKeys.officePhoneNumbers]?.find(phoneNumber => phoneNumber?.is_primary);
    const initialData = Object.assign({}, new Agency(), agency, {
      [AgencyHeaderKeys.primaryShippingAddress]: primaryShippingAddress,
      [AgencyHeaderKeys.primaryPhoneNumber]: primaryPhoneNumber,
      [AgencyHeaderKeys.primaryEmailAddress]: primaryEmailAddress,
    });

    const formData = new Proxy(initialData, {
      set: (target, prop, value, receiver) => {
        const prevValue = target[prop];

        if (value !== prevValue) {
          this.formChangesDetector.handleChange(prop, value, prevValue);
          Reflect.set(target, prop, value, receiver);

          this.formDataSig.update(data => data);

          switch (prop) {
            case AgencyKeys.agencyType:
              const mga = value === AGENCY_TYPE.MGA ? (agency.agency_id ?? null) : agency.mga;
              Object.assign(this.formDataSig(), { [AgencyKeys.mga]: mga });
              break;
          }
        }
        return true;
      },
    });

    this.formDataSig.set(formData);
  };

  successfulCreatedForm(): void {
    this.onSuccessfulCreated();
  }

  updateFieldsData = (values: Partial<Agency>): void => {
    this.onSuccessfulUpdated(Object.keys(values));
  };

  private getChanges = () => {
    const agency = this.formDataSig();

    const updates = {};
    const changes = this.formChangesDetector.getAllChanges();

    changes.forEach(([key]) => {
      const update = agency[key] ?? null;
      const addresses = updates[AgencyKeys.addresses] ?? agency[AgencyKeys.addresses] ?? [];
      const phoneNumbers = updates[AgencyKeys.officePhoneNumbers] ?? agency[AgencyKeys.officePhoneNumbers] ?? [];

      switch (key) {
        case AgencyHeaderKeys.primaryPhoneNumber:
          Object.assign(updates, {
            [AgencyKeys.officePhoneNumbers]: phoneNumbers.map(phoneNumber => {
              const isSame = phoneNumber === update;

              Object.assign(phoneNumber, { is_primary: isSame });
              return phoneNumber;
            }),
          });
          return;

        case AgencyHeaderKeys.primaryBillingAddress:
          Object.assign(updates, {
            [AgencyKeys.addresses]: addresses.map(address => {
              const isSame = address === update;
              Object.assign(address, { is_primary_billing: isSame });
              return address;
            }),
          });
          return;

        case AgencyHeaderKeys.primaryShippingAddress:
          Object.assign(updates, {
            [AgencyKeys.addresses]: addresses.map(address => {
              const isSame = address === update;
              Object.assign(address, { is_primary_shipping: isSame });
              return address;
            }),
          });
          return;

        default:
          Object.assign(updates, { [key]: update });
          return;
      }
    });

    return updates;
  };

  /**
   * Compares agency names trimmed and case-insensitively.
   */
  isDuplicateAgencyName(candidateName: string | null | undefined): boolean {
    const normalized = (candidateName ?? '').trim().toLowerCase();
    if (!normalized) {
      return false;
    }
    const current = this.formDataSig();
    const currentDbId = current?.[BaseModelKeys.dbId];
    const agencies = this.agenciesSig() ?? [];
    return agencies.some(other => {
      if (currentDbId && other?.[BaseModelKeys.dbId] === currentDbId) {
        return false;
      }
      const otherNormalized = (other?.[AgencyKeys.name] ?? '').trim().toLowerCase();
      return otherNormalized === normalized;
    });
  }

  /**
   * Compares agency IDs trimmed and case-insensitively.
   */
  isDuplicateAgencyId(candidateAgencyId: string | null | undefined): boolean {
    const normalized = (candidateAgencyId ?? '').trim().toLowerCase();
    if (!normalized) {
      return false;
    }
    const current = this.formDataSig();
    const currentDbId = current?.[BaseModelKeys.dbId];
    const agencies = this.agenciesSig() ?? [];
    return agencies.some(other => {
      if (currentDbId && other?.[BaseModelKeys.dbId] === currentDbId) {
        return false;
      }
      const otherNormalized = (other?.[AgencyKeys.agencyId] ?? '').trim().toLowerCase();
      return otherNormalized === normalized;
    });
  }

  handleSave = async () => {
    this.startProgress();
    const commands: BatchCommand[] = [];
    const agency = this.formDataSig();
    const rawName = agency?.[AgencyKeys.name];
    if (typeof rawName === 'string') {
      const trimmed = rawName.trim();
      if (trimmed !== rawName) {
        agency[AgencyKeys.name] = trimmed;
      }
    }
    const rawAgencyId = agency?.[AgencyKeys.agencyId];
    if (typeof rawAgencyId === 'string') {
      const trimmedId = rawAgencyId.trim();
      if (trimmedId !== rawAgencyId) {
        agency[AgencyKeys.agencyId] = trimmedId;
      }
    }
    const agencyDbId = agency?.[BaseModelKeys.dbId];
    const updates = this.getChanges();
    const baseAgentCommand: Omit<BatchCommand, BatchCommandKeys.dbId | BatchCommandKeys.data> = {
      [BatchCommandKeys.table]: AGENTS_FIRESTORE_COLLECTION,
      [BatchCommandKeys.toFirestore]: AgentService.toFirestore,
      [BatchCommandKeys.type]: BatchCommandType.update,
    };

    if (!agencyDbId) {
      try {
        const createdAgency = await this.agencyService.create(agency);
        AGENCY_SYSTEM_DIRECTORY_NAMES.forEach(name => this.createSystemFileDirectories(createdAgency.dbId, name));

        this.stopProgress();
        return { agencyDbId: createdAgency?.[BaseModelKeys.dbId], updates };
      } catch (error) {
        this.stopProgress();
        throw error;
      }
    }

    const isMgaChanged = this.formChangesDetector.checkIsFieldChanged(AgencyKeys.mga);
    const isAgencyLeadChanged = this.formChangesDetector.checkIsFieldChanged(AgencyKeys.agencyLead);

    try {
      if (isMgaChanged) {
        let originMgaId = this.formChangesDetector.getInitialValue(AgencyKeys.mga);
        if (!originMgaId) {
          originMgaId = agency?.[AgencyKeys.agencyId];
        }
        const targetMgaId = agency?.[AgencyKeys.mga] ?? null;
        const workSheetName = `${agency?.[AgencyKeys.name]} Transfer`;
        const transferConfig = {
          mode: AgencyHierarchyManagerMode.agencyTransfer,
          workSheetName,
          transferedAgency: agency,
          originMgaId,
          targetMgaId,
          defaultHierarchyChanges: [],
        };

        if (isMgaChanged && agency?.[AgencyKeys.agencyType] === AGENCY_TYPE.MGA) {
          const agencyLeadId = agency?.[AgencyKeys.agencyLead];
          const agencyLead = await this.agentSearchService.getByAgentId(agencyLeadId).catch(() => null);
          agencyLead &&
            Object.assign(agencyLead, {
              [AgentKeys.p_mga_id]: targetMgaId,
              [AgentKeys.manager_id]: null,
            }) &&
            (transferConfig.defaultHierarchyChanges = [[agencyLead]]);
        }
        const results = await this.agentsTransferModalService.transferTeam(transferConfig);

        if (results?.type !== ModalWindowUserAction.apply) {
          throw new Error('abort');
        }
        this._isTransferInProgress$.next(true);

        const hierarchyChanges = results?.data?.hierarchyChanges;
        const targetMgaLeadSelection = results?.data?.targetMgaLeadSelection;
        Object.entries(hierarchyChanges).forEach(([changedAgentDbId, hierarchyChange]) => {
          const change = {
            [BaseModelKeys.actionName]: this.changeSourceTypeService.sourceType(),
          };

          Object.assign(
            change,
            pick(hierarchyChange, [
              AgentKeys.manager_id,
              AgentKeys.p_mga_id,
              AgentKeys.p_agency_id,
              AgentKeys.is_manager,
            ]),
          );

          commands.push(
            Object.assign({}, baseAgentCommand, {
              [BatchCommandKeys.dbId]: changedAgentDbId,
              [BatchCommandKeys.data]: change,
            }),
          );
        });

        if (targetMgaLeadSelection?.targetMgaDbId && targetMgaLeadSelection?.targetMgaLeadAgentId) {
          commands.push({
            [BatchCommandKeys.table]: 'agencies',
            [BatchCommandKeys.type]: BatchCommandType.update,
            [BatchCommandKeys.dbId]: targetMgaLeadSelection.targetMgaDbId,
            [BatchCommandKeys.data]: {
              [BaseModelKeys.actionName]: this.changeSourceTypeService.sourceType(),
              [AgencyKeys.agencyLead]: targetMgaLeadSelection.targetMgaLeadAgentId,
            },
          });
        }
      }

      const initialAgencyLead = this.formChangesDetector.getInitialValue(AgencyKeys.agencyLead);
      if (isAgencyLeadChanged && initialAgencyLead) {
        const agents = await this.agentSearchService.getAgentsByAgentId(initialAgencyLead);
        agents.forEach(agent => {
          const dbId = agent?.[BaseModelKeys.dbId];

          commands.push(
            Object.assign({}, baseAgentCommand, {
              [BatchCommandKeys.dbId]: dbId,
              [AgentKeys.manager_id]: agency?.[AgencyKeys.agencyLead],
            }),
          );
        });
      }

      commands.push({
        [BatchCommandKeys.table]: 'agencies',
        [BatchCommandKeys.type]: BatchCommandType.update,
        [BatchCommandKeys.dbId]: agencyDbId,
        [BatchCommandKeys.data]: updates,
      });

      if (commands?.length) {
        await this.batchOperationsService.runBatchOperations(commands);
      }
      this._isTransferInProgress$.next(false);
      this.toastrService.success('Agency Successfully Updated');
      this.formChangesDetector.clear();
      this.stopProgress();
    } catch (error) {
      this._isTransferInProgress$.next(false);
      this.stopProgress();
      throw error;
    }

    //     if (selectedAgencyStatus && !selectedAgencyStatus?.isAssigned) {
    //       updateDoc(selectedAgencyStatus?.reference as DocumentReference<Lookup>, { [LookupKeys.isAssigned]: true });
    //     }

    return { agencyDbId, updates };
  };

  getSelectedAgencyLeads(agencyId: string): Observable<Agent[]> {
    return this.allAgencyLeads$.pipe(
      map(agents => agents.filter(agent => agencyId && agent[AgentKeys.p_agency_id] == agencyId)),
    );
  }

  setSelectedAgencyStatus = (item): void => {
    this.selectedAgencyStatus$.next(item);
  };

  private mapAgentByName = (rmd: Agent): Agent => {
    const name = [rmd[AgentKeys.p_agent_first_name], rmd[AgentKeys.p_agent_last_name]].filter(Boolean).join(' ');
    const email = rmd[AgentKeys.p_email];

    rmd[AgentKeys.p_agent_name] = name;
    rmd['agent_display_name'] = email ? `${name} (${email})` : name;
    return rmd;
  };

  private sortAgentByName = (a: Agent, b: Agent): number => {
    return a[AgentKeys.p_agent_first_name] < b[AgentKeys.p_agent_first_name]
      ? -1
      : a[AgentKeys.p_agent_first_name] > b[AgentKeys.p_agent_first_name]
        ? 1
        : 0;
  };

  private createSystemFileDirectories(agencyId: string, directoryName: string): void {
    const directory = {
      [AgencyFileDirectoryKeys.agencyDbId]: agencyId,
      [FileDirectoryKeys.name]: directoryName,
      [FileDirectoryKeys.isSystem]: true,
    };
    this.agencyFileDirectoriesService
      .create(
        agencyId,
        Object.assign(directory, { [AgencyFileDirectoryKeys.agencyDbId]: agencyId }) as AgencyFileDirectory,
      )
      .then(() => false)
      .catch(err => {
        this.toastrService.error('Folder is not created!');
        throw err;
      })
      .finally(() => {});
  }
}
